import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PublicPageLayout from '../components/PublicPageLayout';
import { ChevronRight, ScanLine, Shield, Zap, Eye, Heart, Users, Award, Target, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const values = [
  { icon: Zap, title: 'Simplicity', desc: 'Powerful tools, simple interface', color: 'text-amber-500', bg: 'bg-amber-50' },
  { icon: Shield, title: 'Security', desc: 'Bank-grade encryption, never sell your data', color: 'text-emerald-500', bg: 'bg-emerald-50' },
  { icon: Globe, title: 'Australian-First', desc: 'Every feature built for Australian businesses', color: 'text-blue-500', bg: 'bg-blue-50' },
  { icon: Eye, title: 'Transparency', desc: 'No hidden fees, clear pricing', color: 'text-purple-500', bg: 'bg-purple-50' },
];

const stats = [
  { value: '98.5%', label: 'AI Accuracy', icon: Target },
  { value: '10K+', label: 'Receipts Scanned', icon: ScanLine },
  { value: '$0.88/km', label: 'ATO Mileage Rate', icon: Award },
  { value: '24/7', label: 'Cloud Backup', icon: Shield },
];

export default function AboutPage() {
  const { data: page } = useQuery({
    queryKey: ['page', 'about'],
    queryFn: async () => {
      const { data } = await axios.get(`${API}/api/pages/about`);
      return data;
    },
  });

  return (
    <PublicPageLayout>
      <div data-testid="about-page">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white py-20">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #000 1px, transparent 0)', backgroundSize: '40px 40px' }} />
          <div className="max-w-4xl mx-auto px-6 text-center relative">
            <div className="flex items-center gap-2 text-sm text-slate-400 justify-center mb-8">
              <Link to="/" className="hover:text-slate-600 transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-slate-600 font-medium">About Us</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-6">
              <Heart className="w-4 h-4" /> Built in Australia, for Australia
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Smart Expense Tracking,<br />
              <span className="text-emerald-600">Made for Australians</span>
            </h1>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
              ExpensIQ is Australia's intelligent expense tracking platform, purpose-built for freelancers, sole traders, and small businesses navigating the ATO landscape.
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 border-y border-slate-100">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map(s => (
                <div key={s.label} className="text-center">
                  <s.icon className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission & Story */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-semibold uppercase tracking-wider mb-4">
                  <Target className="w-3.5 h-3.5" /> Our Mission
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Making expenses effortless</h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  We believe managing business expenses should be effortless. Too many Australians spend hours sorting receipts, tracking GST, and preparing for tax time. ExpensIQ uses AI to automate the tedious parts so you can focus on growing your business.
                </p>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-semibold uppercase tracking-wider mb-4">
                  <Award className="w-3.5 h-3.5" /> Our Story
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">Built from frustration</h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  ExpensIQ was born from frustration: existing expense trackers weren't built for Australia. They didn't understand GST, didn't align with ATO categories, and couldn't generate BAS-ready reports. We built something better — a platform that speaks your language and knows your tax system.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What Makes Us Different */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-3">What Makes Us Different</h2>
              <p className="text-sm text-slate-500">Purpose-built for the Australian business landscape</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { title: 'AI-Powered', desc: 'GPT receipt scanner with 98.5% accuracy' },
                { title: 'Built for Australia', desc: 'ATO categories, GST, BAS-ready reports' },
                { title: 'Mileage at ATO Rates', desc: '$0.88/km with automatic deductions' },
                { title: 'Accountant Collaboration', desc: 'Secure read-only portal sharing' },
                { title: 'Team-Ready', desc: 'Employee expenses, approvals, spending limits' },
                { title: 'Multi-Currency', desc: '10 currencies with AUD as default' },
              ].map(f => (
                <div key={f.title} className="flex items-start gap-3 bg-white rounded-xl p-5 border border-slate-200/80">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{f.title}</h3>
                    <p className="text-sm text-slate-500 mt-0.5">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Our Values</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {values.map(v => (
                <div key={v.title} className={`${v.bg} rounded-2xl p-6 text-center`}>
                  <v.icon className={`w-7 h-7 ${v.color} mx-auto mb-3`} />
                  <h3 className="text-sm font-bold text-slate-900 mb-1">{v.title}</h3>
                  <p className="text-xs text-slate-500">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Users className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">The Team</h2>
            <p className="text-slate-400 max-w-xl mx-auto mb-8 text-sm leading-relaxed">
              Built by a passionate team of developers, designers, and finance professionals based in Sydney, Australia. We live and breathe Aussie tax law so you don't have to.
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
              <span>Sydney, NSW</span>
              <span className="w-1 h-1 rounded-full bg-slate-600" />
              <span>hello@expensiq.com.au</span>
              <span className="w-1 h-1 rounded-full bg-slate-600" />
              <span>support@expensiq.com.au</span>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Ready to simplify your expenses?</h2>
            <p className="text-sm text-slate-500 mb-8">Start your 7-day free trial. No credit card required.</p>
            <Link to="/register" className="inline-flex px-8 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-colors text-sm" data-testid="about-cta-register">
              Get Started Free
            </Link>
          </div>
        </section>
      </div>
    </PublicPageLayout>
  );
}
